import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  DollarSign,
  Play,
  Loader2,
  Download,
  TrendingUp,
  Users,
  Server,
  Shield,
  Database,
  AlertCircle,
  CheckCircle2,
  BarChart3,
  PieChart,
  Calendar
} from 'lucide-react';
import * as d3 from 'd3';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CostEstimator = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [estimation, setEstimation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  useEffect(() => {
    if (estimation && activeTab === 'breakdown') {
      renderCostBreakdownChart();
    }
    if (estimation && activeTab === 'tco') {
      renderTCOChart();
    }
  }, [estimation, activeTab]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/cost-estimation/latest`);
      setEstimation(response.data.estimation);
    } catch (error) {
      console.log('No cost estimation yet');
    } finally {
      setLoading(false);
    }
  };

  const generateEstimation = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/cost-estimation/generate`, {});
      setEstimation(response.data);
    } catch (error) {
      console.error('Error:', error);
      alert('Error generating cost estimation. Please ensure other Phase 1 modules have been run.');
    } finally {
      setGenerating(false);
    }
  };

  const renderCostBreakdownChart = () => {
    const svg = d3.select('#cost-breakdown-chart');
    svg.selectAll('*').remove();

    if (!estimation?.baseline_costs?.cost_breakdown) return;

    const data = Object.entries(estimation.baseline_costs.cost_breakdown).map(([key, value]) => ({
      category: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: value
    }));

    const width = 600;
    const height = 400;
    const margin = { top: 20, right: 30, bottom: 100, left: 60 };

    const x = d3.scaleBand()
      .domain(data.map(d => d.category))
      .range([margin.left, width - margin.right])
      .padding(0.2);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value)])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const colorScale = d3.scaleOrdinal()
      .domain(data.map(d => d.category))
      .range(['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1']);

    svg.attr('width', width).attr('height', height);

    // Bars
    svg.selectAll('rect')
      .data(data)
      .join('rect')
      .attr('x', d => x(d.category))
      .attr('y', d => y(d.value))
      .attr('width', x.bandwidth())
      .attr('height', d => y(0) - y(d.value))
      .attr('fill', d => colorScale(d.category))
      .attr('rx', 4);

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end')
      .style('font-size', '10px');

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).tickFormat(d => `$${d / 1000}K`));

    // Values on bars
    svg.selectAll('.value-label')
      .data(data)
      .join('text')
      .attr('class', 'value-label')
      .attr('x', d => x(d.category) + x.bandwidth() / 2)
      .attr('y', d => y(d.value) - 5)
      .attr('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('font-weight', 'bold')
      .text(d => `$${(d.value / 1000).toFixed(0)}K`);
  };

  const renderTCOChart = () => {
    const svg = d3.select('#tco-chart');
    svg.selectAll('*').remove();

    if (!estimation?.tco_projections || estimation.tco_projections.length === 0) return;

    const data = estimation.tco_projections[0].yearly_breakdown;

    const width = 800;
    const height = 400;
    const margin = { top: 20, right: 120, bottom: 60, left: 70 };

    const x = d3.scaleLinear()
      .domain([1, data.length])
      .range([margin.left, width - margin.right]);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.cumulative)])
      .nice()
      .range([height - margin.bottom, margin.top]);

    svg.attr('width', width).attr('height', height);

    // Line generator
    const line = d3.line()
      .x(d => x(d.year))
      .y(d => y(d.cumulative))
      .curve(d3.curveMonotoneX);

    // Draw line
    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#3B82F6')
      .attr('stroke-width', 3)
      .attr('d', line);

    // Draw points
    svg.selectAll('circle')
      .data(data)
      .join('circle')
      .attr('cx', d => x(d.year))
      .attr('cy', d => y(d.cumulative))
      .attr('r', 5)
      .attr('fill', '#3B82F6')
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).tickFormat(d => `Year ${d}`).ticks(data.length));

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).tickFormat(d => `$${(d / 1000000).toFixed(1)}M`));

    // Labels on points
    svg.selectAll('.point-label')
      .data(data)
      .join('text')
      .attr('class', 'point-label')
      .attr('x', d => x(d.year))
      .attr('y', d => y(d.cumulative) - 10)
      .attr('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('font-weight', 'bold')
      .text(d => `$${(d.cumulative / 1000000).toFixed(1)}M`);

    // Legend
    svg.append('text')
      .attr('x', width - margin.right + 10)
      .attr('y', margin.top + 20)
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .text('Cumulative TCO');
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Cost Estimation Engine</h2>
            <p className="text-gray-600">Multi-year TCO and project cost models for S/4HANA migration</p>
          </div>
          <button 
            onClick={generateEstimation}
            disabled={generating}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Calculating...</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                <span>Generate Cost Estimate</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Empty State */}
      {!estimation && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <DollarSign className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No Cost Estimation</h3>
          <p className="text-gray-600 mb-6">Generate a comprehensive cost estimate based on Phase 1 findings</p>
          <button 
            onClick={generateEstimation}
            className="bg-green-600 text-white px-8 py-3 rounded-xl hover:bg-green-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Generate Estimate</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {estimation && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <DollarSign className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{formatCurrency(estimation.baseline_costs.total_cost)}</div>
              <div className="text-green-100 text-sm">Baseline Total Cost</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <Users className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{estimation.effort_estimates.total_person_days}</div>
              <div className="text-blue-100 text-sm">Person-Days</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <Calendar className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{estimation.effort_estimates.total_person_months}</div>
              <div className="text-purple-100 text-sm">Person-Months</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <BarChart3 className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{estimation.cost_scenarios.length}</div>
              <div className="text-orange-100 text-sm">Scenarios</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'breakdown', label: 'Cost Breakdown', icon: PieChart },
                { id: 'scenarios', label: 'Scenarios', icon: BarChart3 },
                { id: 'tco', label: 'Multi-Year TCO', icon: Calendar },
                { id: 'narratives', label: 'AI Insights', icon: CheckCircle2 }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center space-x-2">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                      <span>Cost Estimation Summary</span>
                    </h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Total Effort</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {estimation.effort_estimates.total_person_days} days
                        </p>
                        <p className="text-xs text-gray-500">
                          ({estimation.effort_estimates.total_person_months} person-months)
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Baseline Cost</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(estimation.baseline_costs.total_cost)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Recommended Scenario</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {estimation.recommended_scenario.scenario_name}
                        </p>
                        <p className="text-sm text-green-600 font-semibold">
                          {formatCurrency(estimation.recommended_scenario.total_cost)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Effort by Category */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Effort by Category</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {Object.entries(estimation.effort_estimates.summary_by_category).map(([category, days]) => (
                        <div key={category} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <span className="text-gray-700 capitalize">{category.replace(/_/g, ' ')}</span>
                          <span className="font-bold text-gray-900">{days} days</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Cost Drivers */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Top Cost Drivers</h4>
                    <div className="space-y-3">
                      {Object.entries(estimation.baseline_costs.cost_breakdown)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 5)
                        .map(([category, cost]) => {
                          const percentage = (cost / estimation.baseline_costs.total_cost * 100).toFixed(1);
                          return (
                            <div key={category} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-700 capitalize">
                                  {category.replace(/_/g, ' ')}
                                </span>
                                <span className="text-sm font-bold text-gray-900">
                                  {formatCurrency(cost)} ({percentage}%)
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-green-600 h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}

              {/* Cost Breakdown Tab */}
              {activeTab === 'breakdown' && (
                <div className="space-y-6">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Baseline Cost Breakdown</h4>
                    <div className="flex justify-center">
                      <svg id="cost-breakdown-chart"></svg>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {Object.entries(estimation.baseline_costs.cost_breakdown).map(([category, cost]) => {
                      const icons = {
                        implementation_services: Users,
                        internal_fte: Users,
                        infrastructure: Server,
                        licenses: Database,
                        data_migration: Database,
                        security_compliance: Shield,
                        contingency: AlertCircle
                      };
                      const Icon = icons[category] || DollarSign;
                      
                      return (
                        <div key={category} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                          <div className="flex items-center space-x-3 mb-3">
                            <Icon className="w-8 h-8 text-blue-600" />
                            <div>
                              <h5 className="font-bold text-gray-900 capitalize">
                                {category.replace(/_/g, ' ')}
                              </h5>
                              <p className="text-2xl font-bold text-green-600">
                                {formatCurrency(cost)}
                              </p>
                            </div>
                          </div>
                          <div className="text-sm text-gray-600">
                            {((cost / estimation.baseline_costs.total_cost) * 100).toFixed(1)}% of total cost
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Scenarios Tab */}
              {activeTab === 'scenarios' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Scenario Comparison</h4>
                    <p className="text-sm text-gray-600">
                      Compare different migration approaches with varying partner strategies, timelines, and deployment models
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 px-4 py-3 text-left font-bold">Scenario</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Total Cost</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Timeline</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Migration Type</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Partner Strategy</th>
                          <th className="border border-gray-300 px-4 py-3 text-center font-bold">Deployment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estimation.cost_scenarios.map((scenario) => {
                          const isRecommended = scenario.scenario_id === estimation.recommended_scenario.scenario_id;
                          return (
                            <tr key={scenario.scenario_id} className={isRecommended ? 'bg-green-50' : ''}>
                              <td className="border border-gray-300 px-4 py-3">
                                <div className="font-semibold">{scenario.scenario_name}</div>
                                {isRecommended && (
                                  <span className="inline-block mt-1 px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded-full">
                                    RECOMMENDED
                                  </span>
                                )}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-center">
                                <span className="text-lg font-bold text-green-600">
                                  {formatCurrency(scenario.total_cost)}
                                </span>
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-center">
                                {scenario.timeline_months} months
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-center capitalize">
                                {scenario.parameters.migration_type}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-center capitalize">
                                {scenario.parameters.partner_strategy.replace('_', ' ')}
                              </td>
                              <td className="border border-gray-300 px-4 py-3 text-center uppercase">
                                {scenario.parameters.deployment}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Detailed Scenario Cards */}
                  <div className="grid md:grid-cols-3 gap-6">
                    {estimation.cost_scenarios.map((scenario) => {
                      const isRecommended = scenario.scenario_id === estimation.recommended_scenario.scenario_id;
                      return (
                        <div 
                          key={scenario.scenario_id}
                          className={`border-2 rounded-xl p-6 ${
                            isRecommended ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <h5 className="font-bold text-gray-900">{scenario.scenario_name}</h5>
                            {isRecommended && (
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                          <div className="text-3xl font-bold text-green-600 mb-4">
                            {formatCurrency(scenario.total_cost)}
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Timeline:</span>
                              <span className="font-semibold">{scenario.timeline_months} months</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Onsite:</span>
                              <span className="font-semibold">{(scenario.parameters.geo_mix.onsite * 100).toFixed(0)}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Nearshore:</span>
                              <span className="font-semibold">{(scenario.parameters.geo_mix.nearshore * 100).toFixed(0)}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Offshore:</span>
                              <span className="font-semibold">{(scenario.parameters.geo_mix.offshore * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* TCO Tab */}
              {activeTab === 'tco' && (
                <div className="space-y-6">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">5-Year Total Cost of Ownership</h4>
                    <div className="flex justify-center">
                      <svg id="tco-chart"></svg>
                    </div>
                  </div>

                  {estimation.tco_projections.map((projection) => (
                    <div key={projection.scenario_id} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">
                        {projection.scenario_name} - Year by Year Breakdown
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 px-4 py-2 text-left font-bold">Year</th>
                              <th className="border border-gray-300 px-4 py-2 text-right font-bold">Implementation</th>
                              <th className="border border-gray-300 px-4 py-2 text-right font-bold">Infrastructure</th>
                              <th className="border border-gray-300 px-4 py-2 text-right font-bold">AMS</th>
                              <th className="border border-gray-300 px-4 py-2 text-right font-bold">Licenses</th>
                              <th className="border border-gray-300 px-4 py-2 text-right font-bold">Year Total</th>
                              <th className="border border-gray-300 px-4 py-2 text-right font-bold">Cumulative</th>
                            </tr>
                          </thead>
                          <tbody>
                            {projection.yearly_breakdown.map((year) => (
                              <tr key={year.year}>
                                <td className="border border-gray-300 px-4 py-2 font-semibold">Year {year.year}</td>
                                <td className="border border-gray-300 px-4 py-2 text-right">{formatCurrency(year.implementation)}</td>
                                <td className="border border-gray-300 px-4 py-2 text-right">{formatCurrency(year.infrastructure)}</td>
                                <td className="border border-gray-300 px-4 py-2 text-right">{formatCurrency(year.ams)}</td>
                                <td className="border border-gray-300 px-4 py-2 text-right">{formatCurrency(year.licenses)}</td>
                                <td className="border border-gray-300 px-4 py-2 text-right font-bold">{formatCurrency(year.total)}</td>
                                <td className="border border-gray-300 px-4 py-2 text-right font-bold text-green-600">
                                  {formatCurrency(year.cumulative)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-4 text-right">
                        <p className="text-sm text-gray-600">Total 5-Year TCO:</p>
                        <p className="text-3xl font-bold text-green-600">{formatCurrency(projection.total_tco)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* AI Narratives Tab */}
              {activeTab === 'narratives' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold flex items-center space-x-2">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                        <span>CFO Executive Summary</span>
                      </h3>
                      {estimation.ai_narratives.generated_by.includes('GPT') && (
                        <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold rounded-full flex items-center space-x-1">
                          <TrendingUp className="w-3 h-3" />
                          <span>{estimation.ai_narratives.generated_by}</span>
                        </span>
                      )}
                    </div>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">{estimation.ai_narratives.executive_summary}</p>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Key Recommendations</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">
                          Leverage {(estimation.recommended_scenario.parameters.geo_mix.offshore * 100).toFixed(0)}% offshore delivery model for optimal cost-quality balance
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">
                          Consider {estimation.recommended_scenario.parameters.deployment.toUpperCase()} deployment for predictable subscription pricing
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">
                          Plan for {estimation.recommended_scenario.timeline_months} month implementation timeline to balance cost and urgency
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CostEstimator;
